import { Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DtisFormDialogComponent } from '@dtis/angular';
import { TranslocoModule } from '@jsverse/transloco';
import type { CreateBusinessUnitRequest } from '../../../../models';
import { ManagementService } from '../../../../services/management.service';

@Component({
  selector: 'dtis-admin-create-business-unit-dialog',
  imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, TranslocoModule, DtisFormDialogComponent],
  template: `
    <dtis-form-dialog
      title="create-business-unit"
      [saveDisabled]="form.invalid"
      [isLoading]="isSubmitting"
      (saved)="onSubmit()"
      (cancelled)="dialogRef.close()"
    >
      <form [formGroup]="form" class="flex">
        <div class="flex w-full flex-col gap-2">
          <div class="flex w-full">
            <mat-form-field class="w-full" appearance="outline">
              <mat-label>{{ 'business-unit-name' | transloco }}</mat-label>
              <input
                matInput
                formControlName="name"
                required
              />
              @if (form.get('name')?.hasError('required') &&
              form.get('name')?.touched) {
                <mat-error>{{ 'name-is-required' | transloco }}</mat-error>
              }
            </mat-form-field>
          </div>
          <div class="flex w-full">
            <mat-form-field class="w-full" appearance="outline">
              <mat-label>{{ 'description' | transloco }}</mat-label>
              <textarea
                matInput
                formControlName="description"
              ></textarea>
              @if (form.get('description')?.hasError('required') &&
              form.get('description')?.touched) {
                <mat-error>{{ 'description-is-required' | transloco }}</mat-error>
              }
            </mat-form-field>
          </div>
        </div>
      </form>
    </dtis-form-dialog>
  `,
})
export class CreateBusinessUnitDialogComponent {
  private readonly fb = inject(FormBuilder);
  readonly dialogRef = inject(MatDialogRef<CreateBusinessUnitDialogComponent>);
  private readonly managementService = inject(ManagementService);

  isSubmitting = false;

  form = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    description: ['', [Validators.required]],
  });

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const { name, description } = this.form.value;

    if (!name || !description) {
      this.isSubmitting = false;
      return;
    }

    const request: CreateBusinessUnitRequest = { name, description };

    this.managementService.createBusinessUnit(request).subscribe({
      next: (result) => {
        this.dialogRef.close(result);
      },
      error: (error) => {
        console.error('Error creating business unit:', error);
        this.isSubmitting = false;
      },
    });
  }
}
